import { tFunctionType } from 'app/components/localization';
import { getAnonymousDisplayNameById } from 'app/constants/AnonymousDisplayNames';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { LessonBooking, LessonTeacher, Participant, UserProfile } from 'app/constants/schemas';
import { BookingStateConsumer } from 'app/containers/rtc/BookingContext/BookingState';
import { getAllEntitiesOfType } from 'app/models/entities/selectors/next';
import { BookingId, BookingState, BookingType } from 'app/models/rtc/booking/constants';
import { AppState } from 'app/store';
import { EntityStringId } from 'app/types/entity';
import { Map } from 'immutable';
import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

export type GetParticipantNameType = (participantId: string, t: tFunctionType) => string;

interface IProps {
  children: (getParticipantName: GetParticipantNameType) => React.ReactNode;
}

interface IBookingParticipantProps extends IProps {
  bookingId: BookingId;
}

interface IDeps {
  bookingState?: BookingState;
}

interface IConnectDeps {
  getParticipantName: GetParticipantNameType;
}

/**
 * Names are provided by the realtime server, but this component is used as a fallback for fetching the
 * names of call participants when they can't be resolved by the realtime server. For lessons teachers the
 * name on the Teacher entity is used rather than the user's display name.
 *
 * In the case that a participant name is pulled from the store via this component, participant name changes
 * won't be immediately reflected in the call.
 */
const GetParticipantName: React.FunctionComponent<IProps & IConnectDeps> = ({ children, getParticipantName }) => (
  children(getParticipantName) as React.ReactElement<any>
);

const getParticipantName = (
  t: tFunctionType,
  participant?: Participant,
  userProfile?: UserProfile,
  lessonTeacher?: LessonTeacher,
): string => {
  if (
    participant &&
    participant.appData &&
    participant.appData.get('role') === LessonBookingRole.Teacher &&
    lessonTeacher &&
    lessonTeacher.name
  ) {
    return lessonTeacher.name;
  } else if (participant) {
    const {
      anonymousName,
      anonymousNameRank,
    } = participant;

    if (anonymousNameRank && anonymousName) {
      return getAnonymousDisplayNameById(t, anonymousName, anonymousNameRank);
    } else if (userProfile && userProfile.displayName) {
      return userProfile.displayName;
    } else {
      // This should only happen if the user has been (soft) deleted
      return t('page.users.display-name.fallback');
    }
  } else {
    // This should never happen because the participant list should have updated when a peer joined.
    return 'Unknown User';
  }
};

const makeGetParticipantNameFromState = (
  userProfiles?: Map<EntityStringId, UserProfile>,
  // The table for the participants comes from the backend, which guarantees an EntityStringId. But
  // the participant that comes from the realtime server only guarantees string. In practice they should
  // be the same but an assertion is made here to allow either source to be passed into this method.
  participants?: Map<string, Participant>,
  lessonTeacher?: LessonTeacher,
) =>
  (participantId: string, t: tFunctionType) => {
    const participant = participants ? participants.get(participantId) : undefined;

    const userId = participant ? participant.user : undefined;
    const userProfile = (userProfiles && userId) ? userProfiles.get(userId) : undefined;

    return getParticipantName(t, participant, userProfile, lessonTeacher);
  };

const mapStateToBookingParticipantProps = () => createSelector(
  (state: AppState, props: IBookingParticipantProps) => props.bookingId,
  (state: AppState) => getAllEntitiesOfType(state, Participant),
  (state: AppState) => getAllEntitiesOfType(state, UserProfile),
  (state: AppState) => getAllEntitiesOfType(state, LessonTeacher),
  (state: AppState) => getAllEntitiesOfType(state, LessonBooking),

  (bookingId, participants, userProfiles, lessonTeachers, lessonBookings) => {
    const booking = lessonBookings.get(bookingId);
    const lessonTeacher = booking && lessonTeachers.get(booking.teacher);

    const getParticipantNameFromState = makeGetParticipantNameFromState(userProfiles, participants, lessonTeacher);
    return { getParticipantName: getParticipantNameFromState };
  },
);

export const BookingParticipant = connect(mapStateToBookingParticipantProps)(GetParticipantName);

const mapStateToProps = () => createSelector(
  (state: AppState, props: IDeps) => props.bookingState,
  (state: AppState) => getAllEntitiesOfType(state, Participant),
  (state: AppState) => getAllEntitiesOfType(state, UserProfile),
  (state: AppState) => getAllEntitiesOfType(state, LessonTeacher),

  (bookingState, participants, userProfiles, lessonTeachers) => {
    const lessonTeacher: LessonTeacher | undefined = (
      (bookingState && bookingState.bookingType === BookingType.Lesson) ?
        lessonTeachers.get(bookingState.teacherId) :
        undefined
    );

    const getParticipantNameFromState = makeGetParticipantNameFromState(userProfiles, participants, lessonTeacher);
    return { getParticipantName: getParticipantNameFromState };
  },
);

const Container = connect(mapStateToProps)(GetParticipantName);

const GetParticipantNameInjectDependencies: FunctionComponent<IProps> = (props) => (
  <BookingStateConsumer>{bookingState =>
    <Container bookingState={bookingState} {...props} />
  }</BookingStateConsumer>
);

export default GetParticipantNameInjectDependencies;
